/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { LucideIconInput } from '@lucide/angular';

export type V3ContentCardView = 'grid' | 'list';

export const V3_CONTENT_CARD_GRADIENTS = [
  'linear-gradient(135deg, #ede9fe, #c4b5fd)',
  'linear-gradient(135deg, #eff6ff, #bfdbfe)',
  'linear-gradient(135deg, #ecfdf5, #6ee7b7)',
  'linear-gradient(135deg, #f5f3ff, #ddd6fe)',
  'linear-gradient(135deg, #fef9c3, #fde68a)',
  'linear-gradient(135deg, #fff1f2, #fecdd3)',
];

// Keyed by content id so the gradient travels with the card on reorder, instead of belonging to the grid slot.
// Case-insensitive so callers passing lowercased ids agree with callers passing raw ids.
export function v3ContentCardGradient(key: string | null | undefined, fallbackIndex = 0): string {
  if (!key) {
    return V3_CONTENT_CARD_GRADIENTS[fallbackIndex % V3_CONTENT_CARD_GRADIENTS.length];
  }
  const normalizedKey = key.toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalizedKey.length; i++) {
    hash = (hash * 31 + normalizedKey.charCodeAt(i)) % 1000000007;
  }
  return V3_CONTENT_CARD_GRADIENTS[hash % V3_CONTENT_CARD_GRADIENTS.length];
}

export type V3ContentCardStatusTone = 'draft' | 'unpublished' | 'archived' | 'success' | 'warning' | 'danger' | 'neutral';

export interface V3ContentCardStatusBadge {
  label: string;
  tone: V3ContentCardStatusTone;
  icon?: LucideIconInput;
}

export interface V3ContentCardMetaEntry {
  icon?: LucideIconInput;
  text: string;
}

export interface V3ContentCardItem {
  uid: string;
  title: string;
  titleIsPlaceholder?: boolean;
  subtitle?: string;
  thumbnail?: string | null;
  gradient?: string | null;
  coverIcon?: LucideIconInput | null;
  typeLabel?: string;
  typeIcon?: LucideIconInput | null;
  progress?: number | null;
  gradeLabel?: string | null;
  gradePassing?: boolean;
  statusBadge?: V3ContentCardStatusBadge | null;
  meta?: V3ContentCardMetaEntry[];
  /**
   * Chip pinned to the top of the cover in grid view. The list view's cover is a 64px thumbnail with no
   * room for it, so there it falls back to the head of the meta row.
   */
  coverBadge?: V3ContentCardMetaEntry | null;
  /** A deadline that has already passed. Takes the cover's top corner over `coverBadge`. */
  overdueBadge?: V3ContentCardMetaEntry | null;
  /** Badge rendered inline at the end of the meta row, next to dates rather than above the title. */
  metaBadge?: V3ContentCardStatusBadge | null;
  tags?: string[];
  accent?: 'teal';
}

export type V3ContentCardRow = 'badges' | 'subtitle' | 'meta' | 'tags' | 'progress';

/**
 * Rows any card in the layout fills. Every card in that layout then keeps the row's space even when its
 * own data leaves it empty, so one card growing a badge or a deadline never shifts the titles beside it.
 */
export function v3ContentCardReservedRows(
  items: readonly (V3ContentCardItem | null | undefined)[],
  view: V3ContentCardView = 'grid',
): V3ContentCardRow[] {
  const isList = view === 'list';
  const rows = new Set<V3ContentCardRow>();

  for (const item of items) {
    if (!item) {
      continue;
    }
    if (item.statusBadge || (isList && item.typeLabel)) {
      rows.add('badges');
    }
    if (item.subtitle) {
      rows.add('subtitle');
    }
    if (item.meta?.length || item.metaBadge || (isList && (item.coverBadge || item.overdueBadge))) {
      rows.add('meta');
    }
    if (!isList && item.tags?.length) {
      rows.add('tags');
    }
    if (item.gradeLabel || (item.progress !== null && item.progress !== undefined)) {
      rows.add('progress');
    }
  }

  return Array.from(rows);
}
